import React, { useEffect, useState } from 'react';

import { climbBridgeAPI } from '../../../lib/api';
import { HealthLight, Metric } from './BridgeHealth';
import PhotosPanel from './PhotosPanel';
import PlaybackTransport from './PlaybackTransport';


/**
 * The running order for the on-air lane.
 *
 * ClimbAlong publishes no running order and only learns one as each team
 * actually starts, so before a round begins the bridge is guessing — and with
 * no readable qualifying leaderboard it falls back to leaderboard order, which
 * is how a team going out second was published as fifth at Milan. The gallery
 * knows the order in advance; this is where that gets in. Everything reading
 * `startList` downstream picks it up at once.
 */
function RunningOrder({ showId, laneId, startList, onDone, busy, setBusy, setError }) {
  const [draft, setDraft] = useState(null);

  // While an edit is open the list is the operator's; otherwise it follows the
  // bridge, so a team starting re-orders it under them rather than being lost.
  const rows = draft ?? startList;
  const dirty = draft !== null;
  const settable = startList.length > 1;

  function move(index, by) {
    const next = [...rows];
    const to = index + by;
    if (to < 0 || to >= next.length) return;
    [next[index], next[to]] = [next[to], next[index]];
    setDraft(next);
  }

  async function run(label, fn) {
    setBusy(label);
    setError('');
    try {
      await fn();
      setDraft(null);
      await onDone?.();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Action failed');
    } finally {
      setBusy('');
    }
  }

  if (!settable) return null;
  const operatorSet = startList.some(a => a.source === 'operator');

  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-semibold text-white">Running order</h2>
        <div className="flex items-center gap-2">
          {dirty && (
            <button
              onClick={() => run('order', () => climbBridgeAPI.setRunningOrder(showId, laneId, rows.map(a => a.athleteId)))}
              disabled={busy === 'order'}
              className="text-[11px] px-2.5 py-1 rounded border border-blue-700 bg-blue-950/50 text-blue-300 hover:border-blue-600"
            >
              Save order
            </button>
          )}
          {dirty && (
            <button onClick={() => setDraft(null)} className="text-[11px] px-2.5 py-1 rounded border border-[#2a2a2a] bg-[#111111] text-gray-400 hover:border-[#3a3a3a]">
              Cancel
            </button>
          )}
          {!dirty && operatorSet && (
            <button
              onClick={() => run('order', () => climbBridgeAPI.clearRunningOrder(showId, laneId))}
              disabled={busy === 'order'}
              className="text-[11px] px-2.5 py-1 rounded border border-[#2a2a2a] bg-[#111111] text-gray-400 hover:border-[#3a3a3a]"
            >
              Reset to inferred
            </button>
          )}
        </div>
      </div>

      <ol className="border border-[#2a2a2a] rounded divide-y divide-[#2a2a2a] overflow-hidden">
        {rows.map((a, i) => (
          <li key={a.athleteId} className="flex items-center gap-3 px-3 py-2 bg-[#111111]">
            <span className="w-5 text-xs text-gray-500 tabular-nums">{i + 1}</span>
            <span className="flex-1 min-w-0 text-sm text-white truncate">{a.label}</span>
            <SourceTag source={a.source} uncertain={a.uncertain} startedAt={a.startedAt} />
            <div className="flex gap-1">
              <button
                onClick={() => move(i, -1)}
                disabled={i === 0}
                className="px-1.5 py-0.5 text-xs rounded border border-[#2a2a2a] bg-[#0a0a0a] text-gray-400 hover:border-[#3a3a3a] disabled:opacity-30"
                aria-label={`Move ${a.label} earlier`}
              >
                ↑
              </button>
              <button
                onClick={() => move(i, 1)}
                disabled={i === rows.length - 1}
                className="px-1.5 py-0.5 text-xs rounded border border-[#2a2a2a] bg-[#0a0a0a] text-gray-400 hover:border-[#3a3a3a] disabled:opacity-30"
                aria-label={`Move ${a.label} later`}
              >
                ↓
              </button>
            </div>
          </li>
        ))}
      </ol>

      <p className="text-[10px] text-gray-600 mt-1.5 leading-relaxed">
        The order teams go out in, which graphics cut in running order select by. ClimbAlong does not publish one, so
        until a team starts the bridge can only infer it — set it here and every graphic follows.
      </p>
    </section>
  );
}

/**
 * Where a place in the order came from: told to us, observed, or guessed.
 *
 * A set place still shows whether that team has climbed. Without it, setting an
 * order turns every row into "set" and hides the one thing worth knowing while
 * re-ordering mid-round — who has already been on the wall, and therefore whose
 * place is now a matter of record rather than a plan.
 */
function SourceTag({ source, uncertain, startedAt }) {
  const tone = source === 'operator'
    ? 'border-blue-800 text-blue-400'
    : source === 'started'
      ? 'border-green-900 text-green-500'
      : uncertain
        ? 'border-amber-900 text-amber-500'
        : 'border-[#2a2a2a] text-gray-500';
  const label = source === 'operator' ? 'set' : source === 'started' ? 'climbed' : uncertain ? 'guess' : 'seeded';
  return (
    <span className="flex items-center gap-1">
      <span className={`text-[10px] px-1.5 py-0.5 rounded border ${tone}`}>{label}</span>
      {source === 'operator' && startedAt && (
        <span className="text-[10px] px-1.5 py-0.5 rounded border border-green-900 text-green-500" title={`Started ${startedAt}`}>
          climbed
        </span>
      )}
    </span>
  );
}

/**
 * The URL Singular's Category dropdown loads its options from, so the list is
 * always this competition's ticked lanes rather than one pasted for the last.
 */
function CategoryUrl({ streamKey }) {
  const [copied, setCopied] = useState(false);
  if (!streamKey) return null;
  const url = `${window.location.origin}/api/public/climb-bridge/streams/${streamKey}/categories`;
  return (
    <div className="mt-3 px-3 py-2.5 bg-[#111111] border border-[#2a2a2a] rounded">
      <div className="text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">Singular Category dropdown — JSON Source URL</div>
      <div className="flex items-center gap-2">
        <code className="flex-1 text-[11px] text-gray-300 truncate" title={url}>{url}</code>
        <button
          onClick={() => navigator.clipboard?.writeText(url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }).catch(() => {})}
          className="px-2.5 py-1 text-xs rounded border border-[#2a2a2a] bg-[#0a0a0a] text-gray-300 hover:border-[#3a3a3a]"
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <p className="text-[10px] text-gray-600 mt-1.5 leading-relaxed">
        Set once: in Singular, set the root composition's <strong className="text-gray-500">Category</strong> Preset
        Selection to JSON Source: URL and paste this. It belongs to the standings data stream, not to this show, so it stays
        right for every event that uses the same stream — it always lists the lanes ticked on the bridge feeding it.
      </p>
    </div>
  );
}

/**
 * Screen 2 — Live.
 *
 * What an operator looks at during a round. Three things, in order of how often
 * they are needed: is the data good, which lane is on air, and who is in the
 * lower third.
 *
 * The payload preview at the bottom is not decoration. It shows the exact
 * object the composition is receiving, which is what settles "is this a bridge
 * problem or a graphics problem" in one glance.
 */
export default function LivePanel({ summary, showId, onChanged }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [showPayload, setShowPayload] = useState(false);

  const health = summary?.health;
  const lanes = summary?.lanes || [];
  const onAirLaneId = summary?.onAirLaneId;

  useEffect(() => {
    if (!showId) return undefined;
    let cancelled = false;
    const load = () => climbBridgeAPI.state(showId)
      .then(res => { if (!cancelled) setState(res.data); })
      .catch(() => {});
    load();
    const timer = setInterval(load, 2000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [showId]);

  async function act(name, fn) {
    setBusy(name);
    setError('');
    try {
      await fn();
      onChanged?.();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Action failed');
    } finally {
      setBusy('');
    }
  }

  const standings = state?.lanes?.[onAirLaneId]?.standings;
  const startList = state?.startList || [];
  const upstream = health?.upstream || {};
  const publish = health?.publish || {};
  const streams = publish.streams || [];
  const pushesPerMinute = streams.reduce((sum, s) => sum + (s.pushesLastMinute || 0), 0);
  const publishFailures = streams.filter(s => s.configured && s.consecutiveFailures > 0);

  return (
    <div className="space-y-5">
      {error && <div className="px-4 py-3 bg-red-900/30 border border-red-700 rounded text-red-400 text-sm">{error}</div>}

      {health?.bridge?.playback
        ? <PlaybackTransport showId={showId} playback={health.bridge.playback} />
        : <HealthLight health={health} />}

      {/* The numbers that answer "is the data good" */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Metric
          label="Payload age"
          value={upstream.ageSeconds == null ? '—' : `${upstream.ageSeconds}s`}
          tone={upstream.ageSeconds > 15 ? 'bad' : upstream.ageSeconds > 5 ? 'warn' : 'good'}
          hint="Since the last successful poll"
        />
        <Metric label="Upstream p50 / p95" value={upstream.p50Ms == null ? '—' : `${upstream.p50Ms} / ${upstream.p95Ms} ms`} hint="ClimbAlong response time" />
        <Metric
          label="Consecutive failures"
          value={upstream.consecutiveFailures ?? 0}
          tone={upstream.consecutiveFailures > 0 ? 'bad' : 'muted'}
          hint={`Polling every ${upstream.pollIntervalMs ?? '—'} ms`}
        />
        <Metric
          label="Pushes / min"
          value={pushesPerMinute}
          tone={publishFailures.length ? 'bad' : 'default'}
          hint={publishFailures.length ? `${publishFailures.length} stream(s) failing` : `${streams.filter(s => s.configured).length} stream(s) live`}
        />
      </div>

      {/* Which stream each token actually points at.
          "Publishing fine" and "publishing to a stream nobody is watching" look
          identical from failure counts alone — the last four characters are what
          the Singular Data Stream Manager shows, so they can be compared. */}
      {streams.some(s => s.configured) && (
        <section>
          <h2 className="text-sm font-semibold text-white mb-2">Streams</h2>
          <div className="space-y-1">
            {streams.filter(s => s.configured).map(s => (
              <div key={s.stream} className="flex items-center gap-3 px-3 py-2 bg-[#111111] border border-[#2a2a2a] rounded text-xs">
                <span className="text-gray-300 w-24 flex-shrink-0">{s.stream}</span>
                <code className="text-gray-500 flex-1 truncate" title="Compare with the token in Singular's Data Stream Manager">
                  {s.tokenHint || '—'}
                </code>
                <span className={s.consecutiveFailures > 0 ? 'text-red-400' : 'text-gray-600'}>
                  {s.consecutiveFailures > 0 ? `${s.consecutiveFailures} failing` : `${s.pushesLastMinute || 0}/min`}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {(upstream.schemaWarnings > 0 || health?.warnings?.length > 0) && (
        <div className="px-4 py-2.5 bg-amber-900/20 border border-amber-700 rounded text-amber-400 text-xs">
          {upstream.schemaWarnings > 0 && <div>{upstream.schemaWarnings} schema-drift warning(s) — ClimbAlong sent a shape the bridge did not expect. See Diagnostics.</div>}
          {health?.warnings?.length > 0 && <div>{health.warnings.length} data warning(s). See Diagnostics.</div>}
        </div>
      )}

      {/* On-air lane */}
      <section>
        <h2 className="text-sm font-semibold text-white mb-2">Default lane</h2>
        <div className="flex flex-wrap gap-2">
          {lanes.map(lane => (
            <button
              key={lane.laneId}
              disabled={!lane.bindable || busy === `lane-${lane.laneId}`}
              onClick={() => act(`lane-${lane.laneId}`, () => climbBridgeAPI.setOnAirLane(showId, lane.laneId))}
              title={lane.error || (lane.followed === false
                ? `${lane.title} — not being recorded. Putting it on air starts following it.`
                : `${lane.roundTitle || ''} ${lane.title}`)}
              className={`px-4 py-2 text-sm rounded border transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
                lane.laneId === onAirLaneId
                  ? 'bg-blue-600 border-blue-500 text-white'
                  : lane.followed === false
                    ? 'bg-[#111111] border-dashed border-[#2a2a2a] text-gray-600 hover:border-[#3a3a3a]'
                    : 'bg-[#111111] border-[#2a2a2a] text-gray-300 hover:border-[#3a3a3a]'
              }`}
            >
              {lane.title}
              {lane.roundTitle && <span className="ml-1.5 text-[10px] opacity-60">{lane.roundTitle}</span>}
            </button>
          ))}
        </div>
        <p className="text-[10px] text-gray-600 mt-1.5">
          Graphics choose their own lane with Category in Singular. This is only what a graphic shows when its Category is
          blank — and picking a lane that is not ticked starts following it.
        </p>
        <CategoryUrl streamKey={summary?.standingsStreamKey} />
      </section>

      <PhotosPanel showId={showId} photos={summary?.photos} onChanged={onChanged} />

      <RunningOrder
        showId={showId}
        laneId={Number(onAirLaneId)}
        startList={startList}
        onDone={onChanged}
        busy={busy}
        setBusy={setBusy}
        setError={setError}
      />

      {/* Featured athlete */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold text-white">Featured climber</h2>
          <button
            // Off keeps whoever is featured now; it does not clear the graphic.
            onClick={() => act('follow', () => climbBridgeAPI.autoFollow(showId, !summary?.autoFollow))}
            aria-pressed={!!summary?.autoFollow}
            title={summary?.autoFollow ? 'Turn auto-follow off and keep this climber' : 'Follow whoever is on the wall'}
            disabled={busy === 'follow'}
            className={`text-[11px] px-2.5 py-1 rounded border transition-colors ${
              summary?.autoFollow ? 'bg-blue-950/50 border-blue-700 text-blue-300' : 'bg-[#111111] border-[#2a2a2a] text-gray-400 hover:border-[#3a3a3a]'
            }`}
          >
            {summary?.autoFollow ? 'Auto-follow on' : 'Auto-follow'}
          </button>
        </div>

        <select
          value={summary?.selectedAthleteId ?? ''}
          onChange={e => act('athlete', () => climbBridgeAPI.selectAthlete(showId, e.target.value ? Number(e.target.value) : null))}
          className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
        >
          <option value="">— none —</option>
          {startList.map(a => <option key={a.athleteId} value={a.athleteId}>{a.label}</option>)}
        </select>

        {state?.climber && !state.climber.empty && (
          <div className="mt-2 flex items-center gap-4 px-4 py-3 bg-[#111111] border border-[#2a2a2a] rounded">
            <div className="text-2xl font-bold text-white tabular-nums w-10">{state.climber.posDisplay}</div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-white truncate">{state.climber.nameUpper}</div>
              <div className="text-xs text-gray-500">
                {state.climber.country} · {state.climber.score} · {state.climber.pointsDisplay} pts
                {state.climber.onWall && <span className="ml-2 text-green-400">on the wall (inferred)</span>}
              </div>
              {state.climber.currentProblem && (
                <div className="text-xs text-gray-400 mt-0.5">
                  {state.climber.onWall ? 'On' : 'Next'}: <strong className="text-gray-200">{state.climber.currentProblem.title}</strong>
                  {state.climber.currentProblem.state && state.climber.currentProblem.state !== 'pending' && (
                    <span className="text-gray-500"> · {state.climber.currentProblem.state}</span>
                  )}
                  {state.climber.elapsedSeconds != null && (
                    <span className="text-gray-500 tabular-nums"> · {Math.floor(state.climber.elapsedSeconds / 60)}:{String(state.climber.elapsedSeconds % 60).padStart(2, '0')} on the clock</span>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
        <p className="text-[10px] text-gray-600 mt-1.5">
          "On the wall" is inferred from a performance having started and not ended — ClimbAlong publishes no authoritative
          signal for it, so treat auto-follow as a convenience, not a source of truth.
        </p>
      </section>

      {/* Standings preview */}
      {standings?.rows?.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-white mb-2">
            {standings.context?.lane} — {standings.context?.round}
            {standings.provisional && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-900/30 text-amber-400 border border-amber-700">provisional</span>}
          </h2>
          <div className="border border-[#2a2a2a] rounded overflow-hidden overflow-x-auto">
            <table className="w-full text-xs min-w-[520px]">
              <thead className="bg-[#1a1a1a] text-gray-500">
                <tr>
                  <th className="text-left px-3 py-2 font-medium w-10">#</th>
                  <th className="text-left px-3 py-2 font-medium">Athlete</th>
                  <th className="text-left px-3 py-2 font-medium">Score</th>
                  <th className="text-right px-3 py-2 font-medium">Pts</th>
                  {(standings.rows[0].problems || []).map(p => (
                    <th key={p.n} className="text-center px-2 py-2 font-medium w-12">{p.title}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {standings.rows.map(row => (
                  <tr key={row.athleteId} className={`border-t border-[#2a2a2a] ${row.athleteId === summary?.selectedAthleteId ? 'bg-blue-950/30' : ''}`}>
                    <td className="px-3 py-1.5 text-white tabular-nums">
                      {row.posDisplay}
                      {row.posChange !== 0 && (
                        <span className={`ml-1 text-[9px] ${row.posChange > 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {row.posChange > 0 ? '▲' : '▼'}{Math.abs(row.posChange)}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-gray-200">
                      {row.bib && <span className="text-gray-600 mr-1.5 tabular-nums">{row.bib}</span>}
                      {row.name}
                      {row.climbing && <span className="ml-1.5 text-[9px] text-green-400">●</span>}
                      {row.placeholder && <span className="ml-1.5 text-[9px] text-amber-500">not in athlete list</span>}
                    </td>
                    <td className="px-3 py-1.5 text-gray-400 tabular-nums">{row.score}</td>
                    <td className="px-3 py-1.5 text-right text-white tabular-nums">{row.pointsDisplay}</td>
                    {(row.problems || []).map(p => (
                      <td
                        key={p.n}
                        className={`text-center px-2 py-1.5 tabular-nums ${
                          p.state === 'top' ? 'text-green-400' : p.state === 'zone' ? 'text-blue-400' : p.state === 'lowzone' ? 'text-sky-700' : p.state === 'none' ? 'text-gray-600' : 'text-gray-700'
                        }`}
                      >
                        {p.display || '·'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Derived events */}
      {state?.events?.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-white mb-2">Live moments</h2>
          <div className="space-y-1">
            {state.events.map(e => (
              <div key={e.id} className="flex items-center gap-2 px-3 py-1.5 bg-[#111111] border border-[#2a2a2a] rounded text-xs">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-400 w-24">{e.type}</span>
                <span className="text-gray-300">{e.nameUpper}</span>
                {e.problem && <span className="text-gray-500">B{e.problem} {e.display}</span>}
                {e.type === 'rankChange' && <span className="text-gray-500">{e.oldPos} → {e.newPos}</span>}
                <span className="ml-auto text-gray-600">{e.ttlMs}ms TTL</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Raw payload */}
      <section>
        <button onClick={() => setShowPayload(v => !v)} className="text-xs text-blue-500 hover:text-blue-400">
          {showPayload ? 'Hide' : 'Show'} the payload the compositions receive
        </button>
        {showPayload && (
          <pre className="mt-2 p-3 bg-[#0a0a0a] border border-[#2a2a2a] rounded text-[10px] text-gray-400 overflow-auto max-h-96">
            {JSON.stringify(standings ?? { note: 'No standings yet for the on-air lane.' }, null, 2)}
          </pre>
        )}
      </section>
    </div>
  );
}
